import { createTriggerResource } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { LeaseTemplateRepository } from '~/repositories/leaseTemplateRepository';
import { LeasesRenewalsRepository } from '~/repositories/leasesRenewalsRepository';
import { LeaseSettingsRepository } from '~/repositories/settings/leaseSettingsRepository';
import { signingDocumentTemplateRepo } from '~/repositories/signingDocumentTemplateRepository';
import { TemplateRefKind } from '~/swagger/portal/Api';
import type { LeasesRenewalsFilter } from '~/repositories/leasesRenewalsRepository';
import type {
  ChartOfAccountDto,
  CreateLeaseRenewalOfferDto,
  LeaseDto,
  LeaseRenewalOfferItemDto,
  PropertyDto,
  UnitDto,
  LeaseTemplateType,
} from '~/swagger/portal/Api';

const repo = new LeasesRenewalsRepository();
const leaseTemplateRepo = new LeaseTemplateRepository();
const leaseSettingsRepo = new LeaseSettingsRepository();

type Filter = LeasesRenewalsFilter & { leaseId: string };

export type NewLeaseRenewalOfferListItem = LeaseRenewalOfferItemDto & {
  chartOfAccounts: ChartOfAccountDto[];
  leases: LeaseDto[];
  lease?: LeaseDto;
  property?: PropertyDto;
  unit?: UnitDto;
};

export type RenewalTemplateItem = {
  id: string;
  name: string;
  type: LeaseTemplateType;
  kind: TemplateRefKind;
  leaseTemplateCategoryId?: string | null;
};

export type GroupedLeaseTemplatesItem = {
  templates: RenewalTemplateItem[];
  id: string;
  name: string;
  isDefault: boolean;
};

const RENEWAL_TEMPLATE_TYPES = ['lease', 'leaseRenewal', 'information'];

export const fetchGroupedRenewalTemplates = async (): Promise<GroupedLeaseTemplatesItem[]> => {
  const [categories, leaseTemplates, signingTemplates] = await Promise.all([
    leaseTemplateRepo.getLeaseTemplateCategories(),
    leaseTemplateRepo.getLeaseTemplates(),
    signingDocumentTemplateRepo.getSigningTemplates(),
  ]);

  const pdfItems: RenewalTemplateItem[] = (leaseTemplates ?? [])
    .filter((item) => RENEWAL_TEMPLATE_TYPES.includes(item.type))
    .map((item) => ({
      id: item.id,
      name: item.name,
      type: item.type as LeaseTemplateType,
      kind: TemplateRefKind.Pdf,
      leaseTemplateCategoryId: item.leaseTemplateCategoryId,
    }));
  const docxItems: RenewalTemplateItem[] = (signingTemplates ?? [])
    .filter((item) => RENEWAL_TEMPLATE_TYPES.includes(item.templateType))
    .map((item) => ({
      id: item.id,
      name: item.name ?? '',
      type: item.templateType as LeaseTemplateType,
      kind: TemplateRefKind.Docx,
      leaseTemplateCategoryId: item.leaseTemplateCategoryId,
    }));
  const allItems = [...pdfItems, ...docxItems];

  return (categories ?? [])
    .map((category) => ({
      ...category,
      templates: allItems.filter((template) => template.leaseTemplateCategoryId === category.id),
    }))
    .filter((item) => item.templates.length > 0);
};

export const [LeasesRenewalsProvider, useLeasesRenewals] = createMagicDoorContext('LeasesRenewals', () => {
  const [loading, setLoading] = createSignal<boolean>(false);
  const [groupedLeaseTemplates, setGroupedLeaseTemplates] = createSignal<GroupedLeaseTemplatesItem[]>([]);

  const [leasesRenewals, getLeasesRenewals, leasesRenewalsActions] = createTriggerResource(async (params: Filter) => {
    const response = await repo.getRenewalOffers(params.leaseId, {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 100,
      statuses: params.statuses,
    });
    return {
      ...response,
      items: response.items.map((item: LeaseRenewalOfferItemDto) => ({
        ...item,
        chartOfAccounts: response.chartOfAccounts,
        leases: response.leases,
      })) as NewLeaseRenewalOfferListItem[],
    };
  });

  const applyOffer = async (leaseId: string, offerId: string): Promise<void> => {
    const response = await repo.applyOffer(leaseId, offerId);
    leasesRenewalsActions.mutate(
      (prev) =>
        ({
          ...prev,
          items:
            prev?.items?.map((item: NewLeaseRenewalOfferListItem) =>
              item.id === offerId
                ? {
                    ...item,
                    status: response.status,
                  }
                : item
            ) ?? [],
        }) as any
    );
  };

  const createLeaseRenewalOffer = async (leaseId: string, data: CreateLeaseRenewalOfferDto, apply = false) => {
    try {
      setLoading(true);
      if (apply) {
        await repo.createAndApplyLeaseRenewalOffer(leaseId, data);
      } else {
        await repo.createLeaseRenewalOffer(leaseId, data);
      }
    } finally {
      setLoading(false);
    }
  };

  const getGroupedLeaseTemplates = async () => {
    setGroupedLeaseTemplates(await fetchGroupedRenewalTemplates());
  };

  const deleteOffer = async (leaseId: string, offerId: string) => {
    await repo.deleteOffer(leaseId, offerId);
    leasesRenewalsActions.mutate(
      (prev) =>
        ({
          ...prev,
          items:
            prev?.items?.map((item: NewLeaseRenewalOfferListItem) =>
              item.id === offerId
                ? {
                    ...item,
                    status: 'retracted',
                  }
                : item
            ) ?? [],
        }) as any
    );
  };

  const sendSigning = async (leaseId: string, offerId: string) => {
    await repo.sendSigning(leaseId, offerId);
    leasesRenewalsActions.mutate(
      (prev) =>
        ({
          ...prev,
          items:
            prev?.items?.map((item: NewLeaseRenewalOfferListItem) =>
              item.id === offerId
                ? {
                    ...item,
                    status: 'signing',
                  }
                : item
            ) ?? [],
        }) as any
    );
  };

  const isLeaseTemplateSet = async (leaseId: string) => {
    const res = await leaseSettingsRepo.getLeaseSettingsByLevel('lease', leaseId);
    return !!res.templates?.length;
  };

  const getCalculatedLeaseRenewalOffer = async (leaseId: string) => {
    return repo.getCalculatedLeaseRenewalOffer(leaseId);
  };

  return {
    leasesRenewals,
    getLeasesRenewals,
    leasesRenewalsActions,
    applyOffer,
    createLeaseRenewalOffer,
    loading,
    getGroupedLeaseTemplates,
    groupedLeaseTemplates,
    deleteOffer,
    sendSigning,
    isLeaseTemplateSet,
    getCalculatedLeaseRenewalOffer,
  };
});
